import {
  Box,
  Button,
  darken,
  Dialog,
  Group,
  Image,
  lighten,
  luminance,
  MantineProvider,
  MantineThemeOverride,
  Modal,
  Progress,
  Text
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import Cookies from 'js-cookie';
import React, { createContext, useContext, useEffect, useMemo } from "react";
import CookieConsent from "react-cookie-consent";
import ReactGA from 'react-ga';
import { useLocation, useNavigate } from "react-router-dom";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { Socket, connect, io } from "socket.io-client";
import usePersistState from "../hooks/usePersistState";
import ChoosePlan from "../pages/choose_plan";
import PublicPage from "../pages/public";
import Recharge from "../pages/recharge";
import { GET_COMPANY_INTEGRATION } from "../services/companies";
import { GET_ALL_USERS } from "../services/users";
import { useQuery } from "../utility/util";
import { useAuth } from "./auth.context";
import { useLanguage } from "./language.context";
import Intercom from "@intercom/messenger-js-sdk";

interface ConfirmDialogProps {
  (
    props: { text: string; buttonText?: string },
    callback: (props: { confirmed: boolean }) => void
  ): void;
}
interface ConfigsProps {
  primary?: string;
  navbar?: string;
  image?: string;
  icon?: string;
  contrast?: string;
  brand?: string;
  name?: string;
}

interface AgentLanguageOption {
  label: string;
  value: string;
}

type TranslateFn = (key: string) => string;

const AGENT_LANGUAGE_NAMES: Record<string, string> = {
  af: "Afrikaans",
  ar: "Arabic",
  az: "Azerbaijani",
  bg: "Bulgarian",
  bs: "Bosnian",
  ca: "Catalan",
  cs: "Czech",
  cy: "Welsh",
  da: "Danish",
  de: "German",
  el: "Greek",
  en: "English",
  es: "Spanish",
  fa: "Persian",
  fi: "Finnish",
  fil: "Filipino",
  fr: "French",
  gl: "Galician",
  he: "Hebrew",
  hi: "Hindi",
  hr: "Croatian",
  hu: "Hungarian",
  hy: "Armenian",
  id: "Indonesian",
  is: "Icelandic",
  it: "Italian",
  ja: "Japanese",
  kk: "Kazakh",
  kn: "Kannada",
  ko: "Korean",
  lt: "Lithuanian",
  lv: "Latvian",
  mk: "Macedonian",
  mr: "Marathi",
  ms: "Malay",
  ne: "Nepali",
  nl: "Dutch",
  no: "Norwegian",
  pl: "Polish",
  pt: "Portuguese",
  ro: "Romanian",
  ru: "Russian",
  sk: "Slovak",
  sl: "Slovenian",
  sr: "Serbian",
  sv: "Swedish",
  sw: "Swahili",
  ta: "Tamil",
  th: "Thai",
  tr: "Turkish",
  uk: "Ukrainian",
  ur: "Urdu",
  vi: "Vietnamese",
  yue: "Cantonese",
  zh: "Chinese",
};

const AGENT_LANGUAGE_REGIONS: Record<string, string> = {
  "419": "Latin America",
  AM: "Armenia",
  AU: "Australia",
  AZ: "Azerbaijan",
  BA: "Bosnia and Herzegovina",
  BE: "Belgium",
  BG: "Bulgaria",
  BR: "Brazil",
  CA: "Canada",
  CN: "China",
  CZ: "Czech Republic",
  DE: "Germany",
  DK: "Denmark",
  ES: "Spain",
  FI: "Finland",
  FR: "France",
  GB: "United Kingdom",
  GR: "Greece",
  HR: "Croatia",
  IL: "Israel",
  IN: "India",
  IR: "Iran",
  IS: "Iceland",
  IT: "Italy",
  JP: "Japan",
  KE: "Kenya",
  KR: "Korea",
  KZ: "Kazakhstan",
  LT: "Lithuania",
  LV: "Latvia",
  MK: "North Macedonia",
  MY: "Malaysia",
  NL: "Netherlands",
  NO: "Norway",
  NP: "Nepal",
  NZ: "New Zealand",
  PH: "Philippines",
  PL: "Poland",
  PT: "Portugal",
  RO: "Romania",
  RS: "Serbia",
  RU: "Russia",
  SA: "Saudi Arabia",
  SE: "Sweden",
  SI: "Slovenia",
  SK: "Slovakia",
  TH: "Thailand",
  TR: "Turkey",
  TW: "Taiwan",
  UA: "Ukraine",
  US: "United States",
  VN: "Vietnam",
  ZA: "South Africa",
};

const AGENT_LANGUAGE_CODES = [
  "en-US", "en-IN", "en-GB", "en-AU", "en-NZ", "de-DE", "es-ES", "es-419", "hi-IN", "fr-FR", "fr-CA",
  "ja-JP", "pt-PT", "pt-BR", "zh-CN", "ru-RU", "it-IT", "ko-KR", "nl-NL", "nl-BE", "pl-PL", "tr-TR",
  "vi-VN", "ro-RO", "bg-BG", "ca-ES", "th-TH", "da-DK", "fi-FI", "el-GR", "hu-HU", "id-ID", "no-NO",
  "sk-SK", "sv-SE", "lt-LT", "lv-LV", "cs-CZ", "ms-MY", "af-ZA", "ar-SA", "az-AZ", "bs-BA", "cy-GB",
  "fa-IR", "fil-PH", "gl-ES", "he-IL", "hr-HR", "hy-AM", "is-IS", "kk-KZ", "kn-IN", "mk-MK", "mr-IN",
  "ne-NP", "sl-SI", "sr-RS", "sw-KE", "ta-IN", "ur-IN", "yue-CN", "uk-UA"
] as const;

const getCountryFlag = (regionCode?: string) => {
  if (!regionCode) return "🌐";
  if (regionCode === "419") return "🌎";
  if (!/^[A-Z]{2}$/.test(regionCode)) return "🌐";

  return String.fromCodePoint(...Array.from(regionCode).map((char) => 127397 + char.charCodeAt(0)));
};

const translateWithFallback = (translate: TranslateFn | undefined, key: string, fallback: string) => {
  if (!translate) return fallback;

  const translated = translate(key);
  return translated === key ? fallback : translated;
};

const buildAgentLanguageLabel = (value: string, translate?: TranslateFn) => {
  if (value === "multi") {
    return `🌐 ${translateWithFallback(translate, "AGENT_LANGUAGES.MULTILINGUAL", "Multilingual")}`;
  }

  const [languageCode, regionCode] = value.split("-");
  const languageName = translateWithFallback(
    translate,
    `AGENT_LANGUAGES.LANGUAGES.${languageCode.toUpperCase()}`,
    AGENT_LANGUAGE_NAMES[languageCode] ?? languageCode
  );
  const regionName = translateWithFallback(
    translate,
    `AGENT_LANGUAGES.REGIONS.${regionCode}`,
    AGENT_LANGUAGE_REGIONS[regionCode] ?? regionCode
  );
  const flag = getCountryFlag(regionCode);

  return `${flag} ${languageName}${regionName ? ` - ${regionName}` : ""}`;
};

export const createAgentLanguageOptions = (translate?: TranslateFn): AgentLanguageOption[] => AGENT_LANGUAGE_CODES.map((value) => ({
  value,
  label: buildAgentLanguageLabel(value, translate),
}));

export const normalizeAgentLanguages = (value: any, fallback: string[] = []) => {
  const normalized = (Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [])
    .map((item) => `${item}`.trim())
    .filter((item) => item.length > 0);

  return (normalized.length ? normalized : fallback).filter((item, index, items) => items.indexOf(item) === index);
};

export const getAgentLanguageLabels = (
  value: any,
  options: AgentLanguageOption[] = []
) => normalizeAgentLanguages(value, ["en-US"]).map((language) => {
  const option = (options.length ? options : AGENT_LANGUAGE_OPTIONS).find((item) => item.value === language);
  return option?.label ?? language;
});

export const AGENT_LANGUAGE_OPTIONS: AgentLanguageOption[] = createAgentLanguageOptions();

interface AppContextProps {
  confirmDialog: ConfirmDialogProps;
  setColorScheme: (colorScheme: "dark" | "light") => void;
  socket: Socket;
  configs: ConfigsProps;
  mobileApp?: any;
  setConfigs: (vl: ConfigsProps) => any;
  registration?: ServiceWorkerRegistration;
  agents: any[];
  loadAgents: () => void;
  openedSidebar?: boolean;
  toggleOpenedSidebar?: () => void;
  expandImage: (url: string) => void;
  expandText: (text: string) => void;
  newOrder: any;
  agentRoles: any[];
  agentLanguages: AgentLanguageOption[];
  setNewOrder: (params: any) => void;
  notifyGaEvent: (params: any) => void;
  startPlan: (start?: boolean) => void;
  recharge: (start?: boolean) => void;
}

const AppContext = createContext<AppContextProps>({} as AppContextProps);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const lsColorScheme = localStorage.getItem("@awaz-v2/colorScheme") as
    | "light"
    | "dark";

  const [openedConfirm, setOpenedConfirm] = React.useState(null);
  const [newOrder, setNewOrder] = React.useState(null);
  const [startingPlan, setStartingPlan] = React.useState(false);
  const [recharge, setRecharge] = React.useState(false);
  const [importingFiles, setImportingFiles] = React.useState([]);
  const [agents, setAgents] = React.useState<any[]>([]);
  const [configs, setConfigs] = React.useState<ConfigsProps>({
    primary: "#000000",
  });
  const [openImage, setOpenImage] = React.useState<string>("");
  const [openText, setOpenText] = React.useState<string>("");
  const [mobileApp, setMobileApp] = React.useState<any>({});
  const [colorScheme, setColorScheme] = React.useState<"dark" | "light">(
    lsColorScheme || "light"
  );
  const adjustedPrimary = (() => {
    if (configs.primary === "#FFFFFF") {
      return darken(configs.primary, 0.25);
    } else if (configs.primary === "#000000") {
      return lighten(configs.primary, 0.25);
    }
    return configs.primary ?? lighten("#FFFFFF", 0.25);
  })();
  const contrast = (luminance(configs.primary) > 0.5 ? "#000000" : "#FFFFFF")
  const [registration, setRegistration] =
    React.useState<ServiceWorkerRegistration>(null);

  const darken1 = (luminance(adjustedPrimary) > 0.5) ? lighten(adjustedPrimary, 0.4) : darken(adjustedPrimary, 0.65);
  const darken2 = (luminance(adjustedPrimary) > 0.5) ? lighten(adjustedPrimary, 0.3) : darken(adjustedPrimary, 0.4);
  const dark: string[] = (luminance(configs.primary) > 0.5)
    ? [
      darken(adjustedPrimary, 0.8),
      darken(adjustedPrimary, 0.65),
      darken(adjustedPrimary, 0.4),
      darken(adjustedPrimary, 0.2),
      darken(adjustedPrimary, 0.1),
      adjustedPrimary,
      lighten(adjustedPrimary, 0.2),
      lighten(adjustedPrimary, 0.3),
      lighten(adjustedPrimary, 0.4),
      lighten(adjustedPrimary, 0.5),
    ]
    : [
      lighten(adjustedPrimary, 0.5),
      lighten(adjustedPrimary, 0.4),
      lighten(adjustedPrimary, 0.3),
      lighten(adjustedPrimary, 0.2),
      lighten(adjustedPrimary, 0.1),
      adjustedPrimary,
      darken(adjustedPrimary, 0.2),
      darken(adjustedPrimary, 0.4),
      darken(adjustedPrimary, 0.65),
      darken(adjustedPrimary, 0.8),
    ];
  const gray: string[] = (luminance(configs.primary) > 0.5)
    ? [
      contrast,
      darken(contrast, 0.1),
      darken(contrast, 0.2),
      darken(contrast, 0.3),
      darken(contrast, 0.4),
      darken(contrast, 0.5),
      darken(contrast, 0.6),
      darken(contrast, 0.7),
      darken(contrast, 0.8),
      darken(contrast, 0.9),
    ]
    : [
      lighten(contrast, 0.9),
      lighten(contrast, 0.8),
      lighten(contrast, 0.7),
      lighten(contrast, 0.6),
      lighten(contrast, 0.5),
      lighten(contrast, 0.4),
      lighten(contrast, 0.3),
      lighten(contrast, 0.2),
      lighten(contrast, 0.1),
      contrast,
    ];

  const query = useQuery();
  const theme: MantineThemeOverride = {
    fontFamily: "Inter, sans-serif",
    headings: { fontWeight: "bold" },
    primaryColor: "dark",
    colors: {
      gray: gray as any,
      dark: dark as any,
    },
    components: {
      Table: {
        styles: {
          root: {
            border: `1px solid ${darken(configs.primary, 0.4)}`,
            borderRadius: 15,
          },
          th: {
            backgroundColor: darken(configs.primary, 0.4),
            padding: '20px 10px',
            color: gray[1],
            textAlign: 'center',
          },
          td: {
            backgroundColor: darken(configs.primary, 0.3),
            color: gray[1],
            padding: '10px 10px',
          },
        }
      },
      Modal: {
        styles: {
          header: { backgroundColor: dark[7] },
          body: { backgroundColor: dark[7] },
        }
      },
      Checkbox: { styles: { label: { color: gray[6] } } },
      Switch: { styles: { label: { color: gray[6] } } },
      Slider: { styles: { markLabel: { color: gray[6], fontSize: 10 } } },
      TextInput: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      ColorInput: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      Textarea: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      DatePickerInput: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      DateTimePicker: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      TimeInput: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      MultiSelect: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      Select: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      PasswordInput: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      Box: { styles: { input: { color: gray[1], backgroundColor: dark[8] }, label: { color: gray[1] } } },
      Paper: { styles: { root: { borderRadius: '15px', backgroundColor: dark[7] } } },
    },
  }

  let fbc = query.get("fbclid");

  const [socket, setSocket] = React.useState<Socket>(null);

  const { role, roles, user: currentUser, companyHost, loadUserData, user, userData } = useAuth();
  const { str } = useLanguage();

  const { pathname } = useLocation();
  const navigate = useNavigate();


  const gaTrackings = [
    "G-LY81V86E4J",
    companyHost?.ga,
  ].filter(nn => nn);

  const [openedSidebar, setOpenedSidebar] = usePersistState<boolean>(
    "@awaz-v2/opened-sidebar",
    false
  );

  const loadAgents = (setLoading = true) => {
    if (role) {
      GET_ALL_USERS()
        .then((dt) => {
          setAgents(
            dt
              .filter(u => u?.profile?.title !== "Client")
              .sort((a, b) => (a.name > b.name ? 1 : -1))
          );
        })
        .catch((err) => {
          notifications.show({
            title: "Ops",
            message: err.message,
            color: "red",
          });
        });
    }
  };

  const confirmDialog: ConfirmDialogProps = (
    { text, buttonText = str("CONFIRM") },
    callback
  ) => {
    setOpenedConfirm({ text, buttonText, callback });
  };

  const toggleOpenedSidebar = () => {
    setOpenedSidebar(!openedSidebar);
  };

  const notifyGaEvent = (params: any) => {
    for (let trackingId of gaTrackings) {
      ReactGA.event(params, trackingId);
    }
  }

  useEffect(() => {
    GET_COMPANY_INTEGRATION("mobile-app")
      .then((res) => { setMobileApp(res) })
      .catch(err => { })
  }, [userData])

  useEffect(() => {
    for (let trackingId of gaTrackings) {
      ReactGA.initialize(trackingId);
      ReactGA.set({ debug: false });
    }
  }, [JSON.stringify(gaTrackings)]);

  useEffect(() => {
    for (let trackingId of gaTrackings) {
      ReactGA.pageview(pathname, [trackingId]);
    }
  }, [JSON.stringify(gaTrackings), pathname]);

  // useEffect(() => {
  //   if ("serviceWorker" in navigator) {
  //     navigator.serviceWorker
  //       .register("/service-worker.js")
  //       .then((registration) => {
  //         setRegistration(registration);
  //       })
  //       .catch((error) => {
  //         console.error("Service worker registration failed:", error);
  //       });
  //   }
  // }, []);

  useEffect(() => {
    if (fbc) Cookies.set("fbc", fbc);
  }, [fbc])

  useEffect(() => {
  }, [socket]);

  useEffect(() => {
    if (userData?.intercom_app_id && userData?.user?.intercom_hash) {
      Intercom({
        app_id: userData?.intercom_app_id,
        user_id: userData?.user?.id,
        name: userData?.user?.name,
        email: userData?.user?.email,
        created_at: userData?.user?.createdAt,
        user_hash: userData?.user?.intercom_hash,
      });
    }
  }, [userData]);

  useEffect(() => {
    localStorage.setItem("@awaz-v2/colorScheme", colorScheme);
  }, [colorScheme]);

  // useEffect(() => {
  //   if (currentUser) {
  //     const s = io("http://localhost:4001", { auth: { token: currentUser.token, role: role?.id } });
  //     setSocket(s);
  //   }
  // }, [currentUser, role]);

  useEffect(loadAgents, [role]);

  const getJsonb = (item, attKey) => ({...item}[attKey] ?? {...item}[`${attKey}_jsonb`])

  useEffect(() => {
    setConfigs(c => ({
      ...c,
      name: userData?.company?.name,
      primary: getJsonb(userData?.whitelabel?.company, 'appearance')?.colors?.primary ?? getJsonb(companyHost, 'appearance')?.colors?.primary ?? "#3b3d5d",
      navbar: getJsonb(userData?.whitelabel?.company, 'appearance')?.colors?.navbar ?? getJsonb(companyHost, 'appearance')?.colors?.navbar ?? "white",
      image: getJsonb(userData?.whitelabel?.company, 'appearance')?.image ?? getJsonb(companyHost, 'appearance')?.image,
      icon: getJsonb(userData?.whitelabel?.company, 'appearance')?.icon ?? getJsonb(companyHost, 'appearance')?.icon,
    }))
  }, [companyHost, userData])

  const value = useMemo<AppContextProps>(() => {
    return {
      confirmDialog,
      setColorScheme,
      socket,
      mobileApp,
      registration,
      agents,
      openedSidebar,
      loadAgents,
      toggleOpenedSidebar,
      expandImage: (url) => setOpenImage(url),
      expandText: (text) => setOpenText(text),
      newOrder,
      setNewOrder,
      notifyGaEvent,
      agentRoles: [
        { value: "sales", label: str("AGENTS.ROLES.SALES") },
        { value: "customer-support", label: str("AGENTS.ROLES.CUSTOMER_SUPPORT") },
        { value: "other", label: str("AGENTS.ROLES.OTHER") },
      ],
      agentLanguages: createAgentLanguageOptions(str),
      // agentLanguages: [
      //   { value: "ar", label: "Arabic" },
      //   { value: "bg", label: "Bulgarian" },
      //   { value: "zh", label: "Chinese" },
      //   { value: "hr", label: "Croatian" },
      //   { value: "cs", label: "Czech" },
      //   { value: "da", label: "Danish" },
      //   { value: "nl", label: "Dutch" },
      //   { value: "en", label: "English" },
      //   { value: "fi", label: "Finnish" },
      //   { value: "fr", label: "French" },
      //   { value: "de", label: "German" },
      //   { value: "el", label: "Greek" },
      //   { value: "hi", label: "Hindi" },
      //   { value: "hu", label: "Hungarian" },
      //   { value: "id", label: "Indonesian" },
      //   { value: "it", label: "Italian" },
      //   { value: "ja", label: "Japanese" },
      //   { value: "ko", label: "Korean" },
      //   { value: "ms", label: "Malay" },
      //   { value: "no", label: "Norwegian" },
      //   { value: "pl", label: "Polish" },
      //   { value: "pt", label: "Portuguese" },
      //   { value: "ro", label: "Romanian" },
      //   { value: "ru", label: "Russian" },
      //   { value: "sk", label: "Slovak" },
      //   { value: "es", label: "Spanish" },
      //   { value: "sv", label: "Swedish" },
      //   { value: "ta", label: "Tamil" },
      //   { value: "tr", label: "Turkish" },
      //   { value: "uk", label: "Ukrainian" },
      //   { value: "vi", label: "Vietnamese" },
      // ],
      startPlan: (start = true) => {
        setStartingPlan(start);
        if (!start) loadUserData();
      },
      recharge: (start = true) => {
        setRecharge(start);
        if (!start) loadUserData();
      },
      configs: {
        ...configs,
        brand: {
          "black": "#000000",
          "green": "#00DB72",
          "yellow": "#FFD540",
          "violet": "#FF4D17",
          "white": "#FBFBFB",
          "blue": "#0A39FC",
          "pink": "#FC0A6B",
        }[configs.navbar ?? "white"],
        contrast,
      },
      setConfigs: (c) => {
        setConfigs(v => ({ ...v, ...c }));
      },
    };
  }, [
    configs,
    setConfigs,
    confirmDialog,
    setColorScheme,
    str,
    socket,
    newOrder,
    registration,
    agents,
    openedSidebar,
    toggleOpenedSidebar,
  ]);

  return (
    <MantineProvider
      withCssVariables
      theme={theme}
      defaultColorScheme="dark"
    // defaultColorScheme="light"
    // forceColorScheme="dark"
    >
      <AppContext.Provider value={value}>
        <>
          <>
            <Dialog
              opened={Boolean(openedConfirm)}
              withCloseButton={false}
              onClose={() => setOpenedConfirm(null)}
              size="lg"
              radius="md"
              zIndex={9999}
            >
              <Text size="sm" mb="sm">
                {openedConfirm?.text}
              </Text>

              <Group align="flex-end">
                <Button variant="outline" color="gray"
                  onClick={() => setOpenedConfirm(null)}
                >{str("CANCEL")}</Button>
                <Button
                  color="violet"
                  onClick={() => {
                    openedConfirm?.callback &&
                      openedConfirm?.callback({ confirmed: true });
                    setOpenedConfirm(null);
                  }}
                >
                  {openedConfirm?.buttonText}
                </Button>
              </Group>
            </Dialog>
          </>
        </>
        {((companyHost && !companyHost.active) || (userData && roles.length === 0)) ? <PublicPage /> : children}
        {/* <Helmet>
          {configs.name && <title>{configs.name}</title>}
          {configs.icon
            ? <link rel="icon" href={configs.icon} />
            : <link rel="icon" href={"/icon.png"} />}
        </Helmet> */}
        {/* {userData?.user && role?.profile?.title !== "Client" && process.env.REACT_APP_SUPORT_BOARD_ID && <Helmet>
          <script id="chat-init" src={`https://cloud.board.support/account/js/init.js?id=${process.env.REACT_APP_SUPORT_BOARD_ID}&name=${userData.user.name}`}></script>
        </Helmet>} */}
        <Dialog opened={importingFiles.length > 0}>
          {importingFiles.map((imp) => (
            <div>
              <div style={{ textAlign: "right" }}>
                <Text c="violet" size="sm">
                  {imp.done}/{imp.total}
                </Text>
              </div>
              <Progress color="violet" value={imp.percent} />
              <Text size="xs" style={{ textAlign: "right" }}>
                {{ themes: "Importação de pautas" }[imp.type]} -{" "}
                {imp.origin.toUpperCase()}
              </Text>
            </div>
          ))}
        </Dialog>
        <Modal
          opened={!!openImage}
          onClose={() => setOpenImage(null)}
          fullScreen
          size="xl"
        >{openImage && <TransformWrapper
          limitToBounds
        >
          {/* {({ zoomIn, zoomOut, resetTransform, ...rest }) => ( */}
          <TransformComponent>
            <Image
              // onClick={() => zoomIn()}
              src={openImage} height={"90vh"} width="100%" fit="contain" />
          </TransformComponent>
          {/* )} */}
        </TransformWrapper>}</Modal>
        <Modal
          opened={!!openText}
          onClose={() => setOpenText(null)}
          size="md"
        ><Box style={{ border: 10, background: "#F4F4F4" }} p="lg">
            {openText}
          </Box>
        </Modal>
        <Modal
          opened={startingPlan}
          overlayProps={{ backgroundOpacity: 0.5 }}
          onClose={() => setStartingPlan(false)}
          size="90vw"
          withCloseButton={false}
          styles={{ content: { background: `${configs.primary}` } }}
        >
          <ChoosePlan onClose={() => setStartingPlan(false)} />
        </Modal>
        <Modal
          opened={recharge}
          overlayProps={{ backgroundOpacity: 0.5 }}
          onClose={() => setRecharge(false)}
          size="90vw"
          withCloseButton={false}
          styles={{ content: { background: `${configs.primary}` } }}
        >
          <Recharge onClose={() => setRecharge(false)} />
        </Modal>
        <CookieConsent
          style={{
            background: theme.colors?.dark[8] ?? '#12141d',
            fontWeight: 'bold',
            borderTop: `1px solid ${theme.colors?.dark[6]}`,
            color: theme.colors?.gray[0],
          }}
          buttonStyle={{
            color: theme.colors?.gray[0],
            background: 'transparent',
            fontWeight: "bold",
            border: `2px solid ${theme.colors?.gray[0]}`,
            borderRadius: 8,
            padding: '5px 30px'
          }}
        >
          {str("COOKIES_DESCRIPTION")}{" "}
        </CookieConsent>
      </AppContext.Provider>
    </MantineProvider>
  );
};

export const useApp = () => useContext(AppContext);

export const useAgentVariables = () => {
  const [companyVariables, setCompanyVariables] = React.useState([]);

  const { userData } = useAuth();

  useEffect(() => {
    setCompanyVariables([
      { label: "Current Time", value: "CurrentTime" },
      { label: "Contact Name", value: "Contact.Name" },
      { label: "Contact First Name", value: "Contact.FirstName" },
      { label: "Contact Phone", value: "Contact.Phone" },
      { label: "Contact E-mail", value: "Contact.Email" },
      ...(userData?.company?.fields_jsonb ?? []).map(f => ({ label: `Contact.${f.title}`, value: `Contact.${(f.title ?? "")}` }))
    ])
  }, [userData?.company?.fields_jsonb]);

  return companyVariables;
};
